import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE = '/planner-api';

// --- Async Thunks ---

export const fetchOrders = createAsyncThunk(
  'transportOrders/fetchOrders',
  async ({ token, page, limit, filters, sort }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.set('page', page || 1);
      params.set('limit', limit || 20);
      if (filters?.search) params.set('search', filters.search);
      if (filters?.status) params.set('status', filters.status);
      if (filters?.priority) params.set('priority', filters.priority);
      if (filters?.deliveryFrom) params.set('deliveryFrom', filters.deliveryFrom);
      if (filters?.deliveryTo) params.set('deliveryTo', filters.deliveryTo);
      if (filters?.noVehicle) params.set('noVehicle', 'true');
      if (filters?.delayed) params.set('delayed', 'true');
      // Multi-sort: pass `sortBy=field1:dir1,field2:dir2` if there are multiple columns
      if (Array.isArray(sort?.columns) && sort.columns.length > 0) {
        params.set('sortBy', sort.columns.map(c => `${c.field}:${c.dir}`).join(','));
      } else if (sort?.field) {
        params.set('sortField', sort.field);
        if (sort.dir) params.set('sortDir', sort.dir);
      }

      const res = await fetch(`${API_BASE}/transport-orders?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch orders');
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchOrderDetail = createAsyncThunk(
  'transportOrders/fetchOrderDetail',
  async ({ token, id }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/transport-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch order');
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Extract a structured error from a non-2xx fetch Response. Returns the
// parsed JSON body (with `code`/`error`/`field`/`value` from the backend's
// friendlyOTConstraintError) or a fallback `{ error }` string.
async function readErrorBody(res, fallback) {
  try {
    const json = await res.json();
    if (json && typeof json === 'object') return json;
  } catch { /* not JSON — fall through */ }
  return { error: fallback, status: res.status };
}

export const createOrder = createAsyncThunk(
  'transportOrders/createOrder',
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/transport-orders`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        return rejectWithValue(await readErrorBody(res, 'Failed to create order'));
      }
      return await res.json();
    } catch (err) {
      return rejectWithValue({ error: err.message });
    }
  }
);

export const updateOrder = createAsyncThunk(
  'transportOrders/updateOrder',
  async ({ token, id, data }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/transport-orders/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        return rejectWithValue(await readErrorBody(res, 'Failed to update order'));
      }
      return await res.json();
    } catch (err) {
      return rejectWithValue({ error: err.message });
    }
  }
);

export const deleteOrder = createAsyncThunk(
  'transportOrders/deleteOrder',
  async ({ token, id, force = false }, { rejectWithValue }) => {
    try {
      const qs = force ? '?force=1' : '';
      const res = await fetch(`${API_BASE}/transport-orders/${id}${qs}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // Preserva o corpo (código OT_HAS_FOOTPRINT, motivos) para o diálogo
        // decidir se oferece Cancelar/Arquivar ou a purga de admin.
        let body = {};
        try { body = await res.json(); } catch { /* sem corpo */ }
        return rejectWithValue({ status: res.status, ...body });
      }
      return id;
    } catch (err) {
      return rejectWithValue({ error: err.message });
    }
  }
);

export const assignToRoute = createAsyncThunk(
  'transportOrders/assignToRoute',
  async ({ token, orderId, routeId }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/transport-orders/${orderId}/assign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ routeId }),
      });
      if (!res.ok) throw new Error('Failed to assign order');
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const unassignFromRoute = createAsyncThunk(
  'transportOrders/unassignFromRoute',
  async ({ token, orderId }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/transport-orders/${orderId}/unassign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to unassign order');
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// --- Slice ---

const transportOrdersSlice = createSlice({
  name: 'transportOrders',
  initialState: {
    list: [],
    current: null,
    pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    filters: { search: '', status: '', priority: '' },
    sort: { field: 'createdAt', dir: 'desc', columns: [] },
    loading: false,
    error: null,
  },
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // reset to first page on filter change
    },
    setSort(state, action) {
      state.sort = action.payload;
      state.pagination.page = 1;
    },
    setPage(state, action) {
      state.pagination.page = action.payload;
    },
    setLimit(state, action) {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },
    clearCurrent(state) {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    // fetchOrders
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.orders || action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = { ...state.pagination, ...action.payload.pagination };
        } else {
          state.pagination.total = action.payload.total || state.list.length;
          state.pagination.totalPages = action.payload.totalPages || Math.ceil(state.pagination.total / state.pagination.limit);
        }
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // fetchOrderDetail
    builder
      .addCase(fetchOrderDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchOrderDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // createOrder
    builder
      .addCase(createOrder.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
        state.pagination.total += 1;
      });

    // updateOrder
    builder
      .addCase(updateOrder.fulfilled, (state, action) => {
        // Casar pelo id REAL (id ou _id), nunca por `undefined === undefined`
        // — os objetos vêm do backend só com `id`, e a comparação antiga
        // (o._id === payload._id) casava SEMPRE o 1.º item quando ambos eram
        // undefined, corrompendo a OT errada (parecia um "duplicado").
        const pid = action.payload?._id ?? action.payload?.id;
        const idx = pid != null ? state.list.findIndex(o => (o._id ?? o.id) === pid) : -1;
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.current && pid != null && (state.current._id ?? state.current.id) === pid) {
          state.current = action.payload;
        }
      });

    // deleteOrder
    builder
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.list = state.list.filter(o => o._id !== action.payload && o.id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      });

    // assignToRoute
    builder
      .addCase(assignToRoute.fulfilled, (state, action) => {
        const pid = action.payload?._id ?? action.payload?.id;
        const idx = pid != null ? state.list.findIndex(o => (o._id ?? o.id) === pid) : -1;
        if (idx !== -1) state.list[idx] = action.payload;
      });

    // unassignFromRoute
    builder
      .addCase(unassignFromRoute.fulfilled, (state, action) => {
        const pid = action.payload?._id ?? action.payload?.id;
        const idx = pid != null ? state.list.findIndex(o => (o._id ?? o.id) === pid) : -1;
        if (idx !== -1) state.list[idx] = action.payload;
      });
  },
});

export const { setFilters, setSort, setPage, setLimit, clearCurrent } = transportOrdersSlice.actions;

// --- Selectors ---
export const selectOrdersList = (state) => state.transportOrders.list;
export const selectOrdersPagination = (state) => state.transportOrders.pagination;
export const selectOrdersFilters = (state) => state.transportOrders.filters;
export const selectOrdersLoading = (state) => state.transportOrders.loading;
export const selectCurrentOrder = (state) => state.transportOrders.current;

export default transportOrdersSlice.reducer;
